import { Controller } from "stimulus"

export default class Typeahead extends Controller {
  static targets = ["input", "hidden", "results"]

  // Lifecycle

  connect() {
    this.endpointValue = this.element.dataset.endpoint
    this.delayValue = this.element.dataset.delay || 300
    this.itemValueValue = this.resultsTarget.dataset.itemValue
    this.itemLabelValue = this.resultsTarget.dataset.itemLabel || this.itemValueValue
    this.defaultItemValueValue = this.resultsTarget.dataset.defaultItemValue || ""
    this.defaultItemLabelValue = this.resultsTarget.dataset.defaultItemLabel || this.defaultItemValueValue
    this.itemClassValue = this.resultsTarget.dataset.itemClass
    this.itemClassActiveValue = this.resultsTarget.dataset.itemClassActive

    this.close()

    if (!this.endpointValue) {
      throw new Error("data-endpoint is a required attribute.")
    }

    if (!this.itemValueValue) {
      throw new Error("data-value is a required attribute.")
    }

    if (!this.inputTarget.hasAttribute("autocomplete")) {
      this.inputTarget.setAttribute("autocomplete", "off")
    }

    this.inputTarget.addEventListener("keydown", this.onKeyDown)
    this.inputTarget.addEventListener("focus", this.onInputFocus)
    this.inputTarget.addEventListener("blur", this.onInputBlur)
    this.inputTarget.addEventListener("input", debounce(this.onInputChange, this.delayValue))
    this.resultsTarget.addEventListener("mousedown", this.onResultsMouseDown)
    this.resultsTarget.addEventListener("click", this.onResultsClick)

    if (this.inputTarget.hasAttribute("autofocus")) {
      this.inputTarget.focus()
    }

    this.mouseDown = false
    this.readyValue = true
  }

  disconnect() {
    if (this.hasInputTarget) {
      this.inputTarget.removeEventListener("keydown", this.onKeydown)
      this.inputTarget.removeEventListener("blur", this.onInputBlur)
      this.inputTarget.removeEventListener("input", this.onInputChange)
    }

    if (this.hasResultsTarget) {
      this.resultsTarget.removeEventListener("mousedown", this.onResultsMouseDown)
      this.resultsTarget.removeEventListener("click", this.onResultsClick)
    }
  }

  // Events

  onKeyDown = (e) => {
    switch (e.key) {
      case "Escape":
        if (!this.isResultsShown()) return

        this.close()
        this.clearResults()
        e.stopPropagation()
        e.preventDefault()
        break
      case "ArrowDown":
        const siblingBelow = this.nextSibling()
        if (siblingBelow) this.select(siblingBelow)
        e.preventDefault()
        break
      case "ArrowUp":
        const siblingAbove = this.previousSibling()
        if (siblingAbove) this.select(siblingAbove)
        e.preventDefault()
        break
      case "Tab":
      case "Enter":
        const selected = this.getSelectedOption()
        if (selected) this.commit(selected)
        break
    }
  }

  onInputFocus = (e) => {
    if (this.getOptions().length == 0) return
    this.open()
  }

  onInputBlur = (e) => {
    if (this.mouseDown) return
    this.close()
  }

  onInputChange = (e) => {
    this.element.removeAttribute("value")
    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = ""
    }

    const query = this.inputTarget.value.trim()
    if (query && query.length > 0) {
      this.fetchResults(query)
    } else {
      this.close()
      this.clearResults()
    }
  }

  onResultsMouseDown = (e) => {
    this.mouseDown = true
    this.resultsTarget.addEventListener("mouseup", () => {
      this.mouseDown = false
    }, { once: true })
  }

  onResultsClick = (e) => {
    if (!(event.target instanceof Element)) return

    const selected = event.target.closest("li")
    if (selected) {
      this.commit(selected)
    }
  }

  // Helpers

  nextSibling() {
    const opts = this.getOptions()
    const selected = this.getSelectedOption()
    const index = opts.indexOf(selected)
    return opts[index + 1] || opts[0]
  }

  previousSibling() {
    const opts = this.getOptions()
    const selected = this.getSelectedOption()
    const index = opts.indexOf(selected)
    return opts[index - 1] || opts[opts.length - 1]
  }

  select(target) {
    const previous = this.getSelectedOption()
    if (previous) {
      previous.removeAttribute("aria-selected")
      previous.classList.remove(...this.itemClassActiveValue.split(" "))
      previous.classList.add(...this.itemClassValue.split(" "))
    }

    target.setAttribute("aria-selected", "true")
    target.classList.remove(...this.itemClassValue.split(" "))
    target.classList.add(...this.itemClassActiveValue.split(" "))
    this.inputTarget.setAttribute("aria-activedescendant", target.id)
    target.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  commit(selected) {
    if (selected.getAttribute("aria-disabled") === "true") return

    const textValue = selected.getAttribute("data-typeahead-label") || selected.textContent.trim()
    const value = selected.getAttribute("data-typeahead-value") || textValue
    const data = JSON.parse(decodeURIComponent(selected.getAttribute("data-typeahead-json")))

    this.inputTarget.value = textValue

    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = value
      this.hiddenTarget.dispatchEvent(new Event("input"))
      this.hiddenTarget.dispatchEvent(new Event("change"))
    } else {
      this.inputTarget.value = value
    }

    this.inputTarget.focus()
    this.close()
    this.clearResults()

    this.element.dispatchEvent(
      new CustomEvent("commit", {
        bubbles: true,
        detail: { value, textValue, data, selected }
      })
    )
  }

  clearResults() {
    this.resultsTarget.innerHTML = null
  }

  open() {
    if (this.isResultsShown()) return

    this.setResultsShown(true)
    this.element.setAttribute("aria-expanded", "true")
  }

  close() {
    if (!this.isResultsShown()) return

    this.setResultsShown(false)
    this.inputTarget.removeAttribute("aria-activedescendant")
    this.element.setAttribute("aria-expanded", "false")
  }

  async fetchResults(query) {
    const url = this.buildURL(query)

    const response = await fetch(url)
    const data = await response.json()

    this.resultsTarget.innerHTML = this.buildOptions(data)

    if (data.length > 0 || this.defaultItemLabelValue) {
      this.open()
    } else {
      this.close()
    }
  }

  buildURL(query) {
    const url = new URL(this.endpointValue, window.location.href)
    const params = new URLSearchParams(url.search.slice(1))
    params.append("q", query)
    url.search = params.toString()
    return url.toString()
  }

  buildOptions(data) {
    if ((!data || data.length === 0) && this.defaultItemLabelValue) {
      return `<li data-typeahead-value="${this.defaultItemValueValue}" class="${this.itemClassValue}" role="option">${this.defaultItemLabelValue}</li>`
    }

    return data.map(opt => {
      const json = encodeURIComponent(JSON.stringify(opt))
      return `<li data-typeahead-json="${json}" data-typeahead-value="${opt[this.itemValueValue]}" class="${this.itemClassValue}" role="option">${opt[this.itemLabelValue]}</li>`
    }).join("")
  }

  // Getters & Setters

  isResultsShown() {
    return !this.resultsTarget.hidden
  }

  setResultsShown(value) {
    this.resultsTarget.hidden = !value
  }

  getOptions() {
    return Array.from(this.resultsTarget.querySelectorAll("li"))
  }

  getSelectedOption() {
    return this.resultsTarget.querySelector("li[aria-selected=true]")
  }
}

function debounce(fn, delay) {
  let timeoutId = null

  return function(...args) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(fn, delay)
  }
}

